import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { memo, useEffect, useMemo, useRef } from 'react';

import LazyHydration from 'components/LazyHydration';

import useMediaQuery from 'hooks/useMediaQuery';

import toArticleText from 'lib/htmlParsers/parseArticleHtml';
import { scrollToElement } from 'lib/utils';

import { useTopbar } from 'providers/TopbarProvider';

import ArticleNavList from './ArticleNavList';

const OffersList = dynamic(() => import('../OffersList'), { ssr: false });

const ARTICLE_HEADING_LEVEL_ATTR = 'data-heading';
const GAP_BETWEEN_HEADING_AND_TOPBAR_IN_PX = 4;
// Prefer this value as hardcoded to simplify logic of `<TopbarProvider />`
const TOPBAR_MAIN_DESKTOP_HALF_HEIGHT_PX = 74 / 2;
const WAIT_TO_AUTO_SCROLL = 300; // 0.3s

// In some cases the heading tags are used as emphasis in links
const getHeadingsWithoutAnchor = (headings) =>
  Array.from(headings).filter((el) => !el.querySelector('a'));

const MainText = ({ articleId, articleText, gaEventCategory }) => {
  const articleTextContainerRef = useRef(null);
  const { isSm, isMd, isLg } = useMediaQuery();
  const router = useRouter();
  const { topbarMobile } = useTopbar();

  /**
   * We have a lot of articles with very large content and for consistencies
   * and to keep the nav list small we only caught the `h2` headings. If was not
   * find any `h2` then that we will caught the `h3` headings.
   */
  const getArticleHeadings = () => {
    const headingSecondLevel = articleTextContainerRef.current.querySelectorAll(
      `h2[${ARTICLE_HEADING_LEVEL_ATTR}]`
    );

    if (headingSecondLevel.length > 0) {
      return getHeadingsWithoutAnchor(headingSecondLevel);
    }

    const headingThirdLevel = articleTextContainerRef.current.querySelectorAll(
      `h3[${ARTICLE_HEADING_LEVEL_ATTR}]`
    );

    return getHeadingsWithoutAnchor(headingThirdLevel);
  };

  const scrollTo = (offsetTop) => {
    // Mobile and Tablet devices uses the same version of Topbar
    const isMobileOrTablet = (isSm || isMd) && !isLg;
    const gap = isMobileOrTablet
      ? -topbarMobile.height / GAP_BETWEEN_HEADING_AND_TOPBAR_IN_PX
      : TOPBAR_MAIN_DESKTOP_HALF_HEIGHT_PX;

    scrollToElement(offsetTop + gap);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      const { hash } = window.location;

      if (!hash) {
        return;
      }

      const headingIndex = Number(hash.replace('#', ''));

      if (isNaN(headingIndex)) {
        return;
      }

      const elements = getArticleHeadings();
      const heading = elements[headingIndex];

      if (heading) {
        scrollTo(heading.offsetTop);
      }
    }, WAIT_TO_AUTO_SCROLL);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const memoizedArticleText = useMemo(
    () => toArticleText(articleText),
    [articleId]
  );

  return (
    <div className="flex flex-col items-center justify-center">
      <div ref={articleTextContainerRef} className="my-4 w-full lg:mx-10">
        <ArticleNavList
          getArticleHeadings={getArticleHeadings}
          scrollTo={scrollTo}
        />
        {memoizedArticleText}
      </div>
      {router.asPath.includes('black-friday-ao-vivo') && (
        <LazyHydration placeholderSize={{ height: 384, width: '100%' }}>
          <OffersList gaEventCategory={gaEventCategory} />
        </LazyHydration>
      )}
    </div>
  );
};

MainText.propTypes = {
  articleId: PropTypes.number.isRequired,
  articleText: PropTypes.string.isRequired,
  gaEventCategory: PropTypes.string.isRequired,
};

export default memo(MainText);
